import { Fragment, useEffect, useState } from 'react'

import { Dialog, Transition } from '@headlessui/react'
import { cx } from '@linaria/core'
import { useErrorBoundary } from 'react-error-boundary'
import invariant from 'tiny-invariant'

import { useFoodMutation, useFoodMutationAsync } from '~/api-client/food_api'
import { Tweet, TweetReply, User } from '~/api-definition/definition'
import { BackButton } from '~/components/action/BackButton'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { AppImage } from '~/components/data-display/AppImage'
import { Loading } from '~/components/data-display/Loading'
import { CameraIcon, CloseIcon } from '~/components/icons'
import { Header } from '~/components/layout/Header'
import { preprocessImages } from '~/libs/image'
import { anyNgWordIncluded } from '~/libs/ng_words'
import { TweetOrReply } from '~/libs/tweet'
import { userProfileIcon } from '~/libs/user'

import { NGWordModal } from '../NGWordModal'

export type TweetDialogVariant =
  | { type: 'newTweet' }
  | {
      type: 'newReply'
      replyTo: TweetOrReply
    }
  | { type: 'updateTweet'; resource: Tweet }
  | { type: 'updateReply'; resource: TweetReply }

export const TweetDialog: React.FC<{
  userMe?: User
  variant: TweetDialogVariant | undefined
  onClose: () => void
  onSuccess: () => void
}> = (props) => {
  const isUpdate =
    props.variant?.type === 'updateTweet' ||
    props.variant?.type === 'updateReply'

  const { showBoundary } = useErrorBoundary()

  const [processingImages, setProcessingImages] = useState(false)
  const [ngWordsModal, setNgWordsModal] = useState(false)

  const [isSending, setIsSending] = useState(false)
  const [text, setText] = useState(
    props.variant && 'resource' in props.variant
      ? props.variant.resource.text
      : ''
  )
  const [images, setImages] = useState<
    | ({ imageId: string; url: string } | { file: File; url: string })[]
    | undefined
  >(
    props.variant && 'resource' in props.variant
      ? props.variant.resource.images.map((image) => ({
          imageId: image.imageId,
          url: image.imageUrl,
        }))
      : undefined
  )

  useEffect(() => {
    if (props.variant == null) return
    setText(
      props.variant && 'resource' in props.variant
        ? props.variant.resource.text
        : ''
    )
    setImages(
      props.variant && 'resource' in props.variant
        ? props.variant.resource.images.map((image) => ({
            imageId: image.imageId,
            url: image.imageUrl,
          }))
        : undefined
    )
  }, [props.variant])

  useEffect(() => {
    if (anyNgWordIncluded(text)) {
      setNgWordsModal(true)
    }
  }, [text])

  const [isOpenDeletionConfirmModal, setIsOpenDeletionConfirmModal] =
    useState(false)

  const newTweet = useFoodMutation('/tweet/new_tweet', {
    onSuccess: props.onSuccess,
    onFinally: () => {
      setIsSending(false)
    },
  })
  const newTweetImageAsync = useFoodMutationAsync('/tweet/new_tweet_image')
  const updateTweet = useFoodMutation('/tweet/update_tweet', {
    onSuccess: props.onSuccess,
    onFinally: () => {
      setIsSending(false)
    },
  })
  const deleteTweet = useFoodMutation('/tweet/delete_tweet', {
    onSuccess: () => {
      setIsOpenDeletionConfirmModal(false)
      props.onSuccess()
    },
    onFinally: () => {
      setIsSending(false)
    },
  })
  const newReply = useFoodMutation('/tweet/new_reply', {
    onSuccess: props.onSuccess,
    onFinally: () => {
      setIsSending(false)
    },
  })
  const updateReply = useFoodMutation('/tweet/update_reply', {
    onSuccess: props.onSuccess,
    onFinally: () => {
      setIsSending(false)
    },
  })
  const deleteReply = useFoodMutation('/tweet/delete_reply', {
    onSuccess: () => {
      setIsOpenDeletionConfirmModal(false)
      props.onSuccess()
    },
    onFinally: () => {
      setIsSending(false)
    },
  })
  const newReplyImageAsync = useFoodMutationAsync('/tweet/new_reply_image')

  const handleDeleteImage = async (index: number) => {
    setImages((v) => {
      return v?.filter((_, j) => index !== j)
    })
  }

  const handleDelete = async () => {
    if (props.variant == null) return

    switch (props.variant.type) {
      case 'updateTweet':
        deleteTweet({
          tweetId: props.variant.resource.tweetId,
        })
        break
      case 'updateReply':
        deleteReply({
          tweetReplyId: props.variant.resource.tweetReplyId,
        })
        break
    }
  }

  const handleInputImages = async (e: React.FormEvent<HTMLInputElement>) => {
    try {
      setProcessingImages(true)
      const files = e.currentTarget.files
      if (files == null) return

      if (images == null) {
        const pImages = await preprocessImages(
          Array.from(files),
          props.variant?.type === 'newTweet' ||
            props.variant?.type === 'updateTweet'
            ? 'tweet'
            : 'tweet_reply'
        )
        if (pImages == null) return

        setImages(
          pImages.map((file) => {
            return { file, url: URL.createObjectURL(file) }
          })
        )
      } else {
        const pImages = await preprocessImages(
          Array.from(files).slice(0, 20 - images.length),
          props.variant?.type === 'newTweet' ||
            props.variant?.type === 'updateTweet'
            ? 'tweet'
            : 'tweet_reply'
        )
        if (pImages == null) return

        setImages([
          ...images,
          ...pImages.map((file) => {
            return { file, url: URL.createObjectURL(file) }
          }),
        ])
      }
    } catch (e) {
      showBoundary(e)
    } finally {
      setProcessingImages(false)
    }
  }

  const handleSubmit = async () => {
    if (props.variant == null) return
    if (isSending) return

    setIsSending(true)
    const imageIds =
      images == null
        ? images
        : await Promise.all(
            images.map(async (image) => {
              invariant(props.variant != null)

              if ('imageId' in image) return image.imageId
              switch (props.variant.type) {
                case 'newTweet':
                case 'updateTweet': {
                  const res = await newTweetImageAsync({
                    imageFile: image.file,
                  })
                  return res.image.imageId
                }
                case 'newReply':
                case 'updateReply': {
                  const res = await newReplyImageAsync({
                    imageFile: image.file,
                  })
                  return res.image.imageId
                }
              }
            })
          )

    switch (props.variant.type) {
      case 'newTweet':
        newTweet({
          text,
          imageIds,
        })
        break
      case 'newReply':
        const replyTo =
          props.variant.replyTo.type === 'tweet'
            ? { tweetId: props.variant.replyTo.value.tweetId }
            : { tweetReplyId: props.variant.replyTo.value.tweetReplyId }

        newReply({
          ...replyTo,
          text,
          imageIds,
        })
        break
      case 'updateTweet':
        updateTweet({
          tweetId: props.variant.resource.tweetId,
          text,
          imageIds,
        })
        break
      case 'updateReply':
        updateReply({
          tweetReplyId: props.variant.resource.tweetReplyId,
          text,
          imageIds,
        })
        break
    }
  }

  return (
    <div>
      <NGWordModal isOpen={ngWordsModal} setIsOpen={setNgWordsModal} />
      <Loading
        loading={isSending && images?.find((image) => 'file' in image) != null}
        loadingText="Sending..."
      />
      <Loading loading={processingImages} loadingText="Processing..." />
      <Transition appear show={props.variant != null} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={props.onClose}>
          <div className="fixed inset-0">
            <Transition.Child as={Fragment}>
              <Dialog.Panel className="h-fit-screen w-full bg-white relative">
                <Modal
                  title="投稿を削除してよろしいですか"
                  isOpen={isOpenDeletionConfirmModal}
                  setIsOpen={setIsOpenDeletionConfirmModal}
                  actionButtons={
                    <div className="flex gap-x-4 mt-6">
                      <Button
                        type="button"
                        variant="functionWeak"
                        className="w-full"
                        onClick={async () => {
                          setIsOpenDeletionConfirmModal(false)
                        }}
                      >
                        戻る
                      </Button>
                      <Button
                        type="button"
                        variant="functionWeak"
                        className="w-full"
                        onClick={handleDelete}
                      >
                        削除する
                      </Button>
                    </div>
                  }
                />
                <div
                  className="grid h-full"
                  style={{ gridTemplateRows: 'auto 1fr auto auto' }}
                >
                  <Header withoutFixed withoutSpacing>
                    <div className="w-5">
                      <BackButton onClick={props.onClose} />
                    </div>
                    つぶやき投稿
                    <div className="w-5" />
                  </Header>
                  <div className="overflow-auto px-4 pt-5 flex gap-x-4 h-full">
                    <div className="flex-shrink-0 h-full">
                      {props.userMe && (
                        <AppImage
                          variant={{
                            type: 'profile',
                            user: props.userMe,
                          }}
                          width={45}
                          height={45}
                          rounded="full"
                          alt={`${props.userMe.displayName}さんのプロフィール画像`}
                          src={userProfileIcon(props.userMe)}
                        />
                      )}
                    </div>
                    <div className="max-h-full overflow-auto w-full">
                      <textarea
                        className="outline-none w-full h-full"
                        placeholder="フーディーに質問してみよう！&#10;条件を指定してオススメのお店などを聞いてみましょう。&#10;（例、北海道でジンギスカンがおいしいお店、など）"
                        defaultValue={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={14}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-6 px-4 py-4 md:px-0">
                    <label className="w-[50px] h-[50px] rounded border border-[#ccccd3] flex justify-center items-center">
                      <CameraIcon className="fill-[#999]" />
                      <input
                        className="hidden"
                        type="file"
                        accept="image/*,.heic,.heif,.dng"
                        multiple
                        onInput={handleInputImages}
                      />
                    </label>
                    {images?.slice(0, 5).map((image, i, images) => {
                      return (
                        <div key={i} className="relative w-[50px] h-[50px]">
                          <AppImage
                            variant={{
                              type: 'contain',
                            }}
                            width={50}
                            height={50}
                            rounded="none"
                            alt={`アップロード画像${i + 1}`}
                            src={image.url}
                            imageClassName="w-[50px] h-[50px]"
                            className="bg-[#EAEAEA] rounded-md overflow-hidden"
                          />

                          {images.length > 5 && i === 4 ? (
                            <div className="w-full h-full bg-black bg-opacity-40 absolute top-0 left-0 flex items-center justify-center text-white">
                              +{images.length - 4}
                            </div>
                          ) : isUpdate && images.length === 1 ? (
                            <></>
                          ) : (
                            <button
                              type="button"
                              className="rounded-full bg-[#f4f4f4] h-4 w-4 flex items-center justify-center absolute top-1 right-1"
                              onClick={() => handleDeleteImage(i)}
                            >
                              <CloseIcon className="fill-[#666] w-2 h-2" />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  <div
                    className={cx(
                      'px-4 py-4 md:px-0 bg-[#E6E6EA] flex justify-center items-center',
                      isUpdate && 'justify-between'
                    )}
                  >
                    {isUpdate && (
                      <button
                        type="button"
                        className="text-[#666]"
                        onClick={() => setIsOpenDeletionConfirmModal(true)}
                      >
                        投稿を削除する
                      </button>
                    )}
                    <Button
                      variant="primary"
                      disabled={
                        isSending ||
                        ((images == null || images.length === 0) &&
                          text === '') ||
                        anyNgWordIncluded(text)
                      }
                      onClick={handleSubmit}
                    >
                      {isUpdate ? '変更する' : '投稿する'}
                    </Button>
                  </div>
                  <div />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
